import { replaceEqualDeep, useMatches } from '@tanstack/react-router';
import * as React from 'react';

/** What a route declares in `staticData`; the app augments it, and it types TanStack's `staticData` option too. */
export interface RouteStaticData {}

declare module '@tanstack/react-router' {
    // A required key in `RouteStaticData` makes `staticData` mandatory on every route.
    interface StaticDataRouteOption extends RouteStaticData {}
}

export type RouteMatchData = {
    /** `{}` for routes that declare nothing, such as `router-testing`'s, so readers handle absent keys. */
    staticData: Partial<RouteStaticData>;
    /** Decoded, as TanStack hands them over. */
    params: Readonly<Record<string, string>>;
    /**
     * The path with params filled in as a link writes it: encoded (`/parts/details/R%2025%25`), except that a splat's
     * `%2F` comes back as `/` (`/apps/x/a%2Fb` gives `/apps/x/a/b`), and without a trailing `/`. To ask whether a
     * route is current, use `useIsRouteActive` or `useRouteName`.
     */
    pathname: string;
};

/**
 * The matches on screen, root first, leaf last. A layout reads its children through this: the navbar takes the
 * leaf's chrome together with its params, from one entry, so the two cannot disagree.
 *
 * When a navigation starts, the location moves at once, but these stay the leaving page's until the render that shows
 * the next page (once its lazy chunk, `beforeLoad` and loaders are done) or its pending component (after TanStack's
 * default `pendingMs`, a second). So the leaf, never the location, says which page the user is on, and
 * `useRouteMatches().at(-1)?.pathname` is the key for state that resets with the page (an error boundary, open
 * dialogs).
 */
export function useRouteMatches(): ReadonlyArray<RouteMatchData> {
    const previous = React.useRef<ReadonlyArray<RouteMatchData>>(undefined);

    // Keeps the array's identity while nothing in it changes, so a navigation that only moves search or loading state
    // doesn't re-render the callers: `replaceEqualDeep` as TanStack's `structuralSharing` applies it. That option's
    // types demand JSON-serialisable data, and the app's `staticData` holds predicates.
    return useMatches({
        select: (matches) =>
            (previous.current = replaceEqualDeep(
                previous.current,
                matches.map((match): RouteMatchData => ({
                    staticData: match.staticData,
                    params: match.params as Record<string, string>,
                    // TanStack's ends with the `/` of an index route's path, which links leave out.
                    pathname: match.pathname === '/' ? '/' : match.pathname.replace(/\/$/, ''),
                })),
            )),
    });
}
